import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SeriesCard from "@/components/base/SeriesCard";
import NewsCard from "@/components/base/NewsCard";
import SectionHeading from "@/components/base/SectionHeading";
import { seriesList } from "@/mocks/series";
import { newsList } from "@/mocks/news";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = (searchParams.get("q") ?? "").trim();
  const [input, setInput] = useState(query);

  const matchedSeries = useMemo(() => {
    if (!query) return [];
    const keyword = query.toLowerCase();
    return seriesList.filter((item) =>
      [item.title, item.description, item.categoryName, item.tags.join(" "), item.director]
        .join(" ")
        .toLowerCase()
        .includes(keyword),
    );
  }, [query]);

  const matchedNews = useMemo(() => {
    if (!query) return [];
    const keyword = query.toLowerCase();
    return newsList.filter((item) =>
      [item.title, item.summary, item.category, item.tags.join(" ")]
        .join(" ")
        .toLowerCase()
        .includes(keyword),
    );
  }, [query]);

  const total = matchedSeries.length + matchedNews.length;

  const hotSeries = [...seriesList].sort((a, b) => b.popularity - a.popularity).slice(0, 4);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = input.trim();
    if (value) setSearchParams({ q: value });
    else setSearchParams({});
  };

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-8 md:py-12">
      <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-400" aria-label="面包屑">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="font-medium text-foreground-700">搜索结果</span>
      </nav>

      <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          搜索{query ? `“${query}”` : ""}结果 - ai短剧
        </h1>
        <p className="mt-3 text-sm text-foreground-600">
          搜索范围覆盖站内全部 AI 短剧作品与短剧资讯，支持标题、简介、类型与标签的模糊匹配。
        </p>

        <form onSubmit={submit} className="mt-6 flex flex-col gap-3 sm:flex-row" role="search">
          <div className="flex h-12 flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 focus-within:border-primary-400">
            <i className="ri-search-line text-lg text-foreground-400"></i>
            <input
              name="q"
              type="search"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="输入短剧名称、关键词或标签"
              aria-label="搜索短剧与资讯"
              className="h-full w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
          </div>
          <button
            type="submit"
            className="flex h-12 items-center justify-center gap-2 rounded-md bg-primary-500 px-8 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-search-2-line text-lg"></i>
            <span className="whitespace-nowrap">重新搜索</span>
          </button>
        </form>

        {query && (
          <p className="mt-4 text-sm text-foreground-500">
            共找到 <span className="font-semibold text-foreground-900">{total}</span> 条结果，
            其中短剧 <span className="font-semibold text-foreground-900">{matchedSeries.length}</span> 部、
            资讯 <span className="font-semibold text-foreground-900">{matchedNews.length}</span> 篇。
          </p>
        )}
      </div>

      {!query ? (
        <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-search-eye-line text-3xl"></i>
          </span>
          <h2 className="mt-5 font-heading text-lg font-semibold text-foreground-900">
            输入关键词开始搜索
          </h2>
          <p className="mt-2 max-w-md text-sm text-foreground-500">
            试试「科幻」「悬疑」「AI动画」，或者直接搜索某部短剧的名字。
          </p>
        </div>
      ) : total === 0 ? (
        <div className="mt-8">
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-emotion-unhappy-line text-3xl"></i>
            </span>
            <h2 className="mt-5 font-heading text-lg font-semibold text-foreground-900">
              没有找到与「{query}」相关的内容
            </h2>
            <p className="mt-2 max-w-md text-sm text-foreground-500">
              换个关键词试试，或者看看下面这些正在热播的短剧。
            </p>
          </div>

          <section className="mt-10">
            <SectionHeading eyebrow="Hot" title="热门短剧推荐" to="/category" toLabel="浏览全部" />
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:gap-5">
              {hotSeries.map((series) => (
                <SeriesCard key={series.id} series={series} />
              ))}
            </div>
          </section>
        </div>
      ) : (
        <div className="mt-8 flex flex-col gap-12">
          {matchedSeries.length > 0 && (
            <section>
              <div className="mb-5 flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                  <i className="ri-clapperboard-line text-primary-500"></i>
                  短剧作品
                  <span className="text-sm font-normal text-foreground-400">
                    （{matchedSeries.length}）
                  </span>
                </h2>
                <Link
                  to="/category"
                  className="text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
                >
                  去分类页筛选
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 lg:gap-5">
                {matchedSeries.map((series) => (
                  <SeriesCard key={series.id} series={series} />
                ))}
              </div>
            </section>
          )}

          {matchedNews.length > 0 && (
            <section>
              <h2 className="mb-5 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <i className="ri-newspaper-line text-primary-500"></i>
                短剧资讯
                <span className="text-sm font-normal text-foreground-400">
                  （{matchedNews.length}）
                </span>
              </h2>
              <div className="flex flex-col gap-4">
                {matchedNews.map((article) => (
                  <NewsCard key={article.id} article={article} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}